"use client";

import { getShouldUseSoftNavigation } from "@faire/browser-routing/core/getShouldUseSoftNavigation";
import { RoutingContext } from "@faire/browser-routing/core/RoutingContext";
import type {
  IRoutingContext,
  ILinkComponentProps,
} from "@faire/browser-routing/core/types";
import { useRouterOrHistory } from "@faire/browser-routing/core/useRouterOrHistory";
import {
  matchLocale,
  prependLocaleToUrl,
} from "@faire/browser-routing/core/util";
import { getLocation } from "@faire/globals/browser/getLocation";
import { getWindow } from "@faire/globals/browser/getWindow";
import * as H from "history";
import { createPath, parsePath } from "history";
import React from "react";
import {
  Link as ReactLink,
  LinkProps as ReactLinkProps,
} from "react-router-dom";

export type ILinkProps<S = H.LocationState> = Omit<ReactLinkProps<S>, "to"> & {
  to: H.LocationDescriptor<S>;
  skipPrependLocale?: boolean;
  prefetch?: boolean;
};

/**
 * Thin wrapper around React Links (from react-router-dom).
 * The thin wrapper allows us to globally augment all Faire URLs, if we want to.
 */
export const Link = <S extends H.LocationState = H.LocationState>(
  props: ILinkProps<S> & React.RefAttributes<HTMLAnchorElement>
) => {
  const routingContext = React.useContext(RoutingContext);
  const { Link: LinkComponentFromContext = ReactLink } = routingContext;
  const { to, skipPrependLocale, onClick, prefetch, ref, ...otherProps } =
    props;
  const history = useRouterOrHistory();

  const { toStr, augmentedToStr, augmentedTo } = getAugmentedLocation({
    to,
    skipPrependLocale,
    routingContext,
  });

  const linkProps: ILinkComponentProps = {
    innerRef: ref,
    to: augmentedTo,
    onClick: (e) => {
      onClick?.(e);

      if (!matchLocale(toStr, routingContext?.locale)) {
        history.push(augmentedToStr);
        getLocation()?.reload();
      }

      if (!getShouldUseSoftNavigation(routingContext, augmentedToStr)) {
        e.preventDefault();
        getWindow()?.history.pushState(null, "", augmentedToStr);
      }
    },
    // Only forward an explicit `prefetch`. React Router's Link would leak it to
    // the DOM, and an explicit `prefetch: undefined` would override defaults set
    // by injected Link implementations (e.g. NextLinkWithoutPrefetch).
    ...(LinkComponentFromContext === ReactLink || prefetch === undefined
      ? {}
      : { prefetch }),
    ...otherProps,
  };

  return <LinkComponentFromContext {...linkProps} />;
};

type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  prefetch?: boolean;
};

/**
 * Thin wrapper around `<a>` tags.
 * The thin wrapper allows us to globally augment all Faire URLs, if we want to.
 */
export const Anchor: React.FC<
  AnchorProps & React.RefAttributes<HTMLAnchorElement>
> = (props) => {
  const routingContext = React.useContext(RoutingContext);
  const { transformHref } = routingContext;
  const { children, href, ref, ...otherProps } = props;

  const potentiallyPrependLocaleHref = href?.startsWith("/")
    ? prependLocaleToUrl(href, routingContext)
    : href;

  const augmentedHref =
    potentiallyPrependLocaleHref && transformHref
      ? transformHref(potentiallyPrependLocaleHref)
      : potentiallyPrependLocaleHref;

  return (
    <a ref={ref} href={augmentedHref} {...otherProps}>
      {children}
    </a>
  );
};

function withOptionalPrefix(value: string | undefined, prefix: string): string {
  if (!value) {
    return "";
  }

  return value.startsWith(prefix) ? value : `${prefix}${value}`;
}

function getAugmentedLocation({
  to,
  skipPrependLocale,
  routingContext,
}: {
  to: H.LocationDescriptor<H.LocationState>;
  skipPrependLocale: boolean | undefined;
  routingContext: IRoutingContext;
}): {
  toStr: string;
  augmentedToStr: string;
  augmentedTo: H.LocationDescriptor<H.LocationState>;
} {
  const { transformHref } = routingContext;

  if (to == null) {
    return {
      augmentedTo: "",
      toStr: "",
      augmentedToStr: "",
    };
  } else if (typeof to === "object") {
    const augmentedTo = to;
    augmentedTo.pathname = prependLocaleToUrl(`${to.pathname}`, routingContext);

    if (transformHref) {
      const fullPath = createPath({
        pathname: augmentedTo.pathname ?? "",
        search: withOptionalPrefix(augmentedTo.search, "?"),
        hash: withOptionalPrefix(augmentedTo.hash, "#"),
      });
      const transformed = transformHref(fullPath);
      const parsed = parsePath(transformed);
      augmentedTo.pathname = parsed.pathname ?? augmentedTo.pathname;
      augmentedTo.search = parsed.search ?? augmentedTo.search;
    }

    return {
      augmentedTo,
      toStr: to.pathname ?? "",
      augmentedToStr: createPath({
        pathname: augmentedTo.pathname ?? "",
        search: withOptionalPrefix(augmentedTo.search, "?"),
        hash: withOptionalPrefix(augmentedTo.hash, "#"),
      }),
    };
  } else {
    /**
     * for bots language selectors, we do not want to prependLocale to US links
     * for example, if localized path is /careers, we want to direct bots to /careers, not /en/careers
     */
    let augmentedTo = skipPrependLocale
      ? to
      : prependLocaleToUrl(`${to}`, routingContext);

    if (transformHref) {
      augmentedTo = transformHref(augmentedTo);
    }

    return {
      augmentedTo,
      toStr: to,
      augmentedToStr: augmentedTo,
    };
  }
}
